import { urlJoin } from 'url-join-ts';

import { Accommodation, CheapestRate } from '@nocowanie/noc-api';

import { createAxiosInstance } from '@app/common';
import { environment } from '@app/environments/environment';
import {
    fetchPreviewDocument,
    isDateRangeValid,
    sortSectionsByOrder,
} from '@app/helpers/cms.helpers';
import {
    AccommodationItemModel,
    CmsApiResponse,
    Hero,
    HorizontalBanner,
    NocApiAccommodationsResponse,
    PromotedAccommodations,
    PromotedDestinations,
} from '@app/models';
import { CmsServiceBase } from '@app/services/abstracts/cms-service.base';

interface CheapestRateWithDates extends CheapestRate {
    checkIn?: string;
    checkOut?: string;
}

export class CmsHomepageService extends CmsServiceBase {
    readonly #axios = createAxiosInstance();
    private static _instance: CmsHomepageService;

    public static get instance(): CmsHomepageService {
        if (!this._instance) {
            this._instance = new CmsHomepageService();
        }

        return this._instance;
    }

    async getHeroSection(preview = false, documentId?: string): Promise<Hero | null> {
        const populate = {
            config: '*',
            content: {
                populate: ['imageSm', 'imageMd', 'imageLg'],
            },
        };

        try {
            if (preview && documentId) {
                return await fetchPreviewDocument<Hero>({
                    axiosInstance: this.axiosInstance,
                    endpoint: 'heroes',
                    documentId,
                    populate,
                    entityName: 'Hero',
                });
            }

            const params = this.buildStrapiParams({
                sort: 'createdAt:desc',
                filters: {
                    config: {
                        isActive: {
                            $eq: true,
                        },
                    },
                },
                status: preview ? 'draft' : 'published',
                populate,
            });

            const response = await this.axiosInstance.get<CmsApiResponse<Hero[]>>('/heroes', {
                params,
            });

            const heroes = response.data.data;

            if (!heroes || heroes.length === 0) {
                return null;
            }

            const hero = heroes.find(hero =>
                isDateRangeValid(hero, h => !!h.config && !!h.content?.length),
            );

            return hero || null;
        } catch (error) {
            console.warn('Failed to fetch hero section:', error);
            return null;
        }
    }

    async getHorizontalBanner(
        preview = false,
        documentId?: string,
    ): Promise<HorizontalBanner | null> {
        const populate = {
            config: '*',
            image: {
                populate: ['imageSm', 'imageLg'],
            },
        };

        try {
            if (preview && documentId) {
                return await fetchPreviewDocument<HorizontalBanner>({
                    axiosInstance: this.axiosInstance,
                    endpoint: 'horizontal-banners',
                    documentId,
                    populate,
                    entityName: 'HorizontalBanner',
                });
            }

            const params = this.buildStrapiParams({
                sort: 'createdAt:desc',
                filters: {
                    config: {
                        isActive: {
                            $eq: true,
                        },
                    },
                },
                status: preview ? 'draft' : 'published',
                populate,
            });

            const response = await this.axiosInstance.get<CmsApiResponse<HorizontalBanner[]>>(
                '/horizontal-banners',
                {
                    params,
                },
            );

            const banners = response.data.data;

            if (!banners?.length) {
                return null;
            }

            const banner = banners.find(banner => isDateRangeValid(banner, h => !!h.config));

            return banner || null;
        } catch (error) {
            console.warn('Failed to fetch horizontal banner:', error);
            return null;
        }
    }

    async getDestinationSuggestions(
        preview = false,
        documentId?: string,
    ): Promise<PromotedDestinations | null> {
        const populate = {
            config: '*',
            headline: '*',
            sections: {
                populate: {
                    icon: '*',
                    destinations: {
                        populate: ['image'],
                    },
                },
            },
        };

        try {
            if (preview && documentId) {
                return await fetchPreviewDocument<PromotedDestinations>({
                    axiosInstance: this.axiosInstance,
                    endpoint: 'promoted-destinations',
                    documentId,
                    populate,
                    entityName: 'Promoted destinations',
                    postProcess: data => {
                        sortSectionsByOrder(data);
                        return data;
                    },
                });
            }

            const params = this.buildStrapiParams({
                sort: 'createdAt:desc',
                filters: {
                    config: {
                        isActive: {
                            $eq: true,
                        },
                    },
                },
                status: preview ? 'draft' : 'published',
                populate,
            });

            const response = await this.axiosInstance.get<CmsApiResponse<PromotedDestinations[]>>(
                '/promoted-destinations',
                {
                    params,
                },
            );

            const promotedDests = response.data.data;

            if (!promotedDests || promotedDests.length === 0) {
                return null;
            }

            const promotedDestinations = promotedDests.find(destination =>
                isDateRangeValid(destination, dest => !!dest.config && !!dest.sections?.length),
            );

            if (promotedDestinations) {
                sortSectionsByOrder(promotedDestinations);
            }

            return promotedDestinations || null;
        } catch (error) {
            console.warn('Failed to fetch destination suggestions:', error);
            return null;
        }
    }

    private mapNocApiAccommodationToModel(accommodation: Accommodation): AccommodationItemModel {
        const cheapestRateWithDates = accommodation.cheapestRate as CheapestRateWithDates;

        return {
            id: accommodation.id ?? '',
            name: accommodation.name ?? '',
            path: accommodation.path ?? '',
            settlementName: accommodation.settlementName ?? '',
            distanceFromCityCentre: accommodation.distanceFromCenter ?? null,
            ratingsAverage: accommodation.ratingsAverage ?? undefined,
            ratingDescription: accommodation.ratingDescription,
            ratingsCount: accommodation.ratingsCount ?? 0,
            cheapestRate: accommodation.cheapestRate
                ? {
                      rate: accommodation.cheapestRate.rate ?? 0,
                      baseRate: accommodation.cheapestRate.baseRate ?? 0,
                      guests: accommodation.cheapestRate.guests ?? 0,
                      children: accommodation.cheapestRate.children ?? 0,
                      childrenAges: accommodation.cheapestRate.childrenAge,
                      duration: accommodation.cheapestRate.duration ?? 0,
                      currency: accommodation.cheapestRate.currency ?? 'PLN',
                      checkIn: cheapestRateWithDates?.checkIn,
                      checkOut: cheapestRateWithDates?.checkOut,
                  }
                : undefined,
            images: accommodation.images ?? [],
        } as AccommodationItemModel;
    }

    async getFeaturedAccommodations(
        preview = false,
        documentId?: string,
    ): Promise<PromotedAccommodations | null> {
        const populate = {
            config: '*',
            headline: '*',
            sections: {
                populate: {
                    icon: '*',
                },
            },
        };

        try {
            if (preview && documentId) {
                return await fetchPreviewDocument<PromotedAccommodations>({
                    axiosInstance: this.axiosInstance,
                    endpoint: 'promoted-accommodations',
                    documentId,
                    populate,
                    entityName: 'Promoted accommodations',
                    postProcess: async data => {
                        sortSectionsByOrder(data);
                        await this.fetchAndMapAccommodations(data, documentId);
                        return data;
                    },
                });
            }

            const params = this.buildStrapiParams({
                sort: 'createdAt:desc',
                filters: {
                    config: {
                        isActive: {
                            $eq: true,
                        },
                    },
                },
                status: preview ? 'draft' : 'published',
                populate,
            });

            const response = await this.axiosInstance.get<CmsApiResponse<PromotedAccommodations[]>>(
                '/promoted-accommodations',
                {
                    params,
                },
            );

            const promotedAccommodations = response.data.data;

            if (!promotedAccommodations?.length) {
                return null;
            }

            const accommodations = promotedAccommodations.find(accommodation =>
                isDateRangeValid(accommodation, acc => !!acc.config && !!acc.sections?.length),
            );

            if (!accommodations) {
                return null;
            }

            sortSectionsByOrder(accommodations);

            await this.fetchAndMapAccommodations(accommodations);

            return accommodations;
        } catch (error) {
            console.warn('Failed to fetch featured accommodations:', error);
            return null;
        }
    }

    private async fetchAndMapAccommodations(
        accommodations: PromotedAccommodations,
        documentId?: string,
    ): Promise<void> {
        try {
            const nocApiUrl = urlJoin(
                environment.NOC_API_END_POINT,
                'api',
                'accommodations',
                'listing',
                'abo-ota',
            );

            const params: Record<string, string> = {
                cms: 'promoted-accommodations',
                'cms-field-path': 'sections.*.accommodationsIDs',
                limit: '80',
            };

            if (documentId) {
                params['cms-document-id'] = documentId;
            }

            const nocApiResponse = await this.#axios.get<NocApiAccommodationsResponse>(nocApiUrl, {
                params,
            });

            const accommodationsData = nocApiResponse.data['hydra:member'];

            const accommodationsMap = new Map(
                accommodationsData.map(acc => [acc.id?.toString() ?? '', acc]),
            );

            accommodations.sections = accommodations.sections.map(section => ({
                ...section,
                accommodations: section.accommodationsIDs
                    .map(id => {
                        const nocApiAccommodation = accommodationsMap.get(id.toString());
                        return nocApiAccommodation
                            ? this.mapNocApiAccommodationToModel(nocApiAccommodation)
                            : null;
                    })
                    .filter(acc => acc !== null),
            }));
        } catch (error) {
            console.warn('Failed to fetch accommodation details from NOC API:', error);
        }
    }
}
